// THE STAGE'S ONE WAY INTO HISTORY.
//
// The App Router patches history.pushState / replaceState so a URL written from
// outside it still reaches its own idea of where the page is (usePathname, and
// the URL every router.refresh() asks the server for). It skips that whenever
// the state handed in already carries its own marker (`__NA`) — so a write that
// spread the live history.state into the new one moved the address bar but not
// the router, and the next refresh fetched the OLD address and wrote it back
// over the new one. So: the stage writes only its own keys, and Next copies its
// marker and tree in itself (and keeps ours when it rewrites the entry later).
//
// A write that keeps the same URL (re-stamping the entry after a refresh) may
// carry Next's keys: nothing about the address changes, so there is nothing for
// the router to learn.

export interface StageEntryState {
  osNav: { path: string; viewerIndex: number | null };
  stageEntry: string;
  /** a monotonic index along this tab's stage history: Back and Forward read
   *  their direction from it when the two places are not related in the tree */
  stageIdx: number;
}

export function stageIdxOf(state: unknown): number | null {
  const v = (state as { stageIdx?: unknown } | null)?.stageIdx;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** write a stage entry: only our keys, so Next's patch learns the address. */
export function writeStageEntry(kind: "push" | "replace", state: StageEntryState, url: string): void {
  const own = { osNav: state.osNav, stageEntry: state.stageEntry, stageIdx: state.stageIdx };
  if (kind === "push") window.history.pushState(own, "", url);
  else window.history.replaceState(own, "", url);
}

/** change only the address of the entry the person is standing on (a query key
 *  dropped, a view added), keeping our keys and letting Next's patch learn it. */
export function replaceStageUrl(url: string): void {
  const s = (window.history.state ?? {}) as Record<string, unknown>;
  const own: Record<string, unknown> = {};
  for (const k of ["osNav", "stageEntry", "stageIdx"]) if (k in s) own[k] = s[k];
  window.history.replaceState(own, "", url);
}
